// Client-side PostHog init. Runs on every page load in the browser.
// Error tracking + session replay live here now (they replaced Sentry).
// https://posthog.com/docs/libraries/next-js

import posthog from 'posthog-js'

// Browser noise that is never actionable: a benign ResizeObserver notice, the
// opaque cross-origin "Script error." (no stack, usually a browser extension),
// the AbortError every browser throws when a video is paused while play() is
// still pending, and React hydration mismatches.
//
// The hydration ones (#418 and its "Hydration failed" / "Text content does not
// match" siblings) are, in production, overwhelmingly caused by visitors'
// DOM-injecting extensions — wallets, Grammarly, translators — not our render:
// the homepage hydrates cleanly in dev, <html>/<body> already carry
// suppressHydrationWarning, they land almost only on the entry page where an
// extension mutates the DOM first, and the minified args are document-level
// ("HTML"). A REAL hydration bug fires in dev (React warns there, unminified),
// and a spike in broken hydration would still surface in the $exception_filtered
// counter below — so dropping these loses no real signal.
const IGNORED_BROWSER_ERRORS = [
  /ResizeObserver loop/,
  /^Script error\.?$/,
  /The play\(\) request was interrupted/,
  /Minified React error #(418|423|425)/,
  /Hydration failed because/,
  /Text content does not match server-rendered HTML/,
]

// The documented local setup is `vercel env pull .env.local`, so every
// developer running `next dev` holds a real production PostHog key — and with no
// gate here, their browsing went into the production project. Not just errors:
// pageviews, autocapture and session replay too (19 localhost pageviews on `/`
// and 16 localhost exceptions in a single August day).
//
// Prefer VERCEL_ENV over NODE_ENV: a Vercel *preview* deploy also reports
// NODE_ENV=production, so NODE_ENV alone would keep filing preview traffic as
// production. NODE_ENV is the fallback for where VERCEL_ENV isn't set (a plain
// `next build && next start`, CI), and it fails safe — production still reports.
//
// NEXT_PUBLIC_* is inlined at BUILD time, so this is decided when the bundle is
// built. A runtime env override does nothing, which is what made the last
// attempt to test this a false negative — verify with `next build`, not
// `next dev`.
const vercelEnv = process.env.NEXT_PUBLIC_VERCEL_ENV
const isProduction = vercelEnv
  ? vercelEnv === 'production'
  : process.env.NODE_ENV === 'production'

if (!isProduction) {
  // Say so out loud. The server half counts what it drops via
  // $exception_filtered; there is no equivalent here because the SDK is never
  // initialised, so a console line is the only signal a developer gets that
  // analytics is deliberately off.
  console.info(
    `[analytics] PostHog disabled outside production (env: ${vercelEnv ?? process.env.NODE_ENV})`,
  )
} else {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    defaults: '2025-11-30',
    enable_heatmaps: true,
    // Error tracking (autocapture unhandled errors + rejections) — the Sentry replacement.
    capture_exceptions: true,
    before_send: (event) => {
      if (event?.event !== '$exception') return event
      const values = event.properties?.$exception_list ?? []
      const matched = (Array.isArray(values) ? values : []).find(
        (v: { value?: unknown }) =>
          typeof v?.value === 'string' &&
          IGNORED_BROWSER_ERRORS.some((pattern) => pattern.test(v.value as string)),
      )
      if (!matched) return event
      // Count what we drop. "Script error." is what the browser reports for ANY
      // cross-origin script without CORS headers — including our own bundle if a
      // CDN ever stops sending them. Silently dropping would turn that into a
      // total client-exception blackout with no signal that anything changed.
      posthog.capture('$exception_filtered', {
        reason: String((matched as { value?: unknown }).value).slice(0, 120),
      })
      return null
    },
    // Session replay is enabled at the project level (by the PostHog wizard);
    // keep it on client-side. Set to true to opt a session out.
    disable_session_recording: false,
  })
}
